import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {MTAApi, MTAPort} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from 'react'
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const mtaApi = new MTAApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface MTASendMailPopupProps {
    onClose : OnClose
}

export const MTASendMailPopup = ({onClose} : MTASendMailPopupProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The email to send
     */
    const [body, setBody] = useState<string>("");
    /**
     * If true, body should be MIME
     */
    const [isMime, setIsMime] = useState<boolean>(false);
    /**
     * The subject
     */
    const [subject, setSubject] = useState<string>("");
    /**
     * The from header
     */
    const [from, setFrom] = useState<string>("");
    /**
     * The envelope sender
     */
    const [sender, setSender] = useState<string>("");
    /**
     * The comma seperated list of recipients
     */
    const [recipients, setRecipients] = useState<string>("");
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true if the message was sent
     */
    const [sent, setSent] = useState<boolean>(false);
    /**
     * Ref for subject input field
     */
    const subjectInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    const send = () =>
    {
        // reset error message
        setErrorMessage(undefined);

        setSending(true);
        setSent(false)

        // split and trim
        const recipientsList = recipients.split(",").map(r => r.trim());

        const callback = isMime ?
            async () => {return await mtaApi.mTAControllerSendEmailMime(
                MTAPort.Mpa,
                sender,
                recipientsList,
                new File([new Blob([body], { type: 'text/plain' })], "mail.eml", {type: "text/plain"}))} :
            async () => {return await mtaApi.mTAControllerSendEmail(
                MTAPort.Mpa,
                subject,
                from,
                body,
                undefined,
                sender,
                recipientsList,
                recipientsList)};

            callback().then(() => {
                setSent(true)
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error sending mail", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSending(false);
            });
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               onEntered={() => subjectInputRef.current?.focus()}
               size="xl"
               fullscreen="lg-down"
               scrollable={true}
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("mta.send-mail")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        {sent &&
                            <RenderAlert message={t('mta.email-sent')} variant="success"/>
                        }

                        <FloatingLabel label={t("mta.subject")} className="text-secondary mb-3">
                            <Form.Control type="text" value={subject} placeholder={t("mta.subject")}
                                          ref={subjectInputRef}
                                          onChange={e => setSubject(e.target.value)} disabled={isMime}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("mta.from")} className="text-secondary mb-3">
                            <Form.Control type="text" value={from} placeholder={t("mta.from")}
                                          onChange={e => setFrom(e.target.value)} disabled={isMime}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("mta.sender")} className="text-secondary mb-3">
                            <Form.Control type="text" value={sender} placeholder={t("mta.sender")}
                                          onChange={e => setSender(e.target.value)}/>
                        </FloatingLabel>

                        <FloatingLabel label={t("mta.recipients")} className="text-secondary mb-3">
                            <Form.Control type="text" value={recipients} placeholder={t("mta.recipients")}
                                          onChange={e => setRecipients(e.target.value)}/>
                        </FloatingLabel>

                        <Form.Group className="mb-3">
                            <Form.Check className="text-secondary" type="switch" label={t("mta.body-is-mime")}
                                        checked={isMime}
                                        onChange={e => setIsMime(e.target.checked)}/>
                        </Form.Group>

                       <Form.Group className="mb-3">
                            <Form.Label className="text-secondary">{t("mta.body")}</Form.Label>
                            <Form.Control
                                className="font-monospace white-space-pre"
                                as="textarea"
                                onChange={e => setBody(e.target.value)}
                                value={body}
                                rows={5}
                            />
                        </Form.Group>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={send}
                        disabled={StringUtils.isBlank(recipients) || StringUtils.isBlank(body) ||
                            (!isMime && (StringUtils.isBlank(subject) || StringUtils.isBlank(from)))}>
                    {sending && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {sending ? t("common.sending") : t("common.send")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
